/**
 * デザイン比較(2026-09-22 発注者依頼。D-132)。管理者だけ。
 *
 * 4つのテーマ(現行・A・B・E)を、実際の画面で見比べる。
 *  - 画面は **本物**(ダッシュボード／オペレーターマスタ／シフト表)を実データで出す。ただし **読み取り専用**:
 *    ダッシュボードはもともと書き込みが無い。オペレーターマスタとシフト表は readOnly で、登録・編集・保存の操作を出さない
 *    (書き込みの処理はすべて readOnly の判定を通る。注意書きだけにしていない)
 *  - テーマの切り替えは **一時的**(保存しない)。この画面を離れると正式なテーマに戻る
 *  - 正式に使うときは「外観設定」(システム設定)で適用する
 * テーマと画面は URL(?theme= &screen=)に持つので、戻るで戻れる・URL を共有すると同じ見え方になる。
 */
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '@core/components/ui/PageHeader';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import HomePage from '@core/dashboard/HomePage';
import OperatorsPage from '@core/masters/OperatorsPage';
import ShiftMonthTab from '@core/staff/ShiftMonthTab';
import { useTheme } from './ThemeProvider';
import { THEMES, THEME_IDS, isThemeId, type ThemeId } from './themes';

type Screen = 'dashboard' | 'operators' | 'shift';
const SCREENS: { key: Screen; label: string }[] = [
  { key: 'dashboard', label: 'ダッシュボード' },
  { key: 'operators', label: 'オペレーターマスタ' },
  { key: 'shift', label: 'シフト表' },
];
const isScreen = (v: string | null): v is Screen => SCREENS.some((s) => s.key === v);

export default function DesignPreviewPage() {
  const { officialTheme, setPreviewTheme } = useTheme();
  const [params, setParams] = useSearchParams();
  const themeParam = params.get('theme');
  const theme: ThemeId = isThemeId(themeParam) ? themeParam : officialTheme;
  const screenParam = params.get('screen');
  const screen: Screen = isScreen(screenParam) ? screenParam : 'dashboard';

  // この画面にいる間だけ、選んだテーマで表示する(保存しない)
  useEffect(() => {
    setPreviewTheme(theme);
  }, [theme, setPreviewTheme]);
  // 離れたら正式なテーマに戻す
  useEffect(() => () => setPreviewTheme(null), [setPreviewTheme]);

  const change = (patch: { theme?: ThemeId; screen?: Screen }) => {
    const p = new URLSearchParams(params);
    if (patch.theme) p.set('theme', patch.theme);
    if (patch.screen) {
      p.set('screen', patch.screen);
      // 前の画面の選択(オペレーターの詳細など)は持ち越さない
      p.delete('op');
      p.delete('mobile');
    }
    setParams(p, { replace: !patch.screen });
  };

  return (
    <div className={`design-preview${screen === 'operators' ? ' is-fill' : ''}`}>
      <PageHeader title="デザイン比較" meta="4つのテーマを、実際の画面(実データ・読み取り専用)で見比べます" />
      <section className="dp-bar" aria-label="プレビューの設定">
        <div className="dp-bar-status">
          <StatusBadge kind="info" label="プレビュー中" />
          <span>
            テーマの切り替えは一時的です(保存しません)。この画面を離れると、いまのテーマ「<b>{THEMES[officialTheme].label}</b>」に戻ります
          </span>
        </div>
        <div className="dp-bar-controls">
          <SegmentedControl label="テーマ" showLabel options={THEME_IDS.map((id) => ({ key: id, label: THEMES[id].short }))} value={theme} onChange={(v) => change({ theme: v as ThemeId })} />
          <SegmentedControl label="画面" showLabel options={SCREENS} value={screen} onChange={(v) => change({ screen: v as Screen })} />
          <Link className="ui-btn ui-btn-secondary ui-btn-sm dp-apply" to={`/settings/system?theme=${theme}#appearance`}>
            このテーマを使う(外観設定へ)
          </Link>
        </div>
        <p className="dp-bar-note">実データを読み取り専用で表示しています。登録・編集・保存の操作は出しません(ダッシュボードのリンクを押すと、その画面へ移ってプレビューは終わります)</p>
      </section>

      <div className="dp-screen" data-screen={screen}>
        {screen === 'dashboard' && <HomePage />}
        {screen === 'operators' && <OperatorsPage readOnly />}
        {screen === 'shift' && (
          <div className="screen full">
            <ShiftMonthTab readOnly onOpenDate={() => undefined} />
          </div>
        )}
      </div>
    </div>
  );
}
